import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, forwardRef, signal } from '@angular/core';
import { NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { IsNonEmptyArrayPipe, HasSetValuePipe } from '@newroom-connect/library/pipes';
import { ButtonType, ActionRole } from '@newroom-connect/library/enums';
import { ILanguageOption } from '@newroom-connect/library/interfaces';
import { fadeInOutAnimation, growAnimation } from '@newroom-connect/library/animations';
import { OverflowTargetDirective } from '@newroom-connect/library/directives';

import { ButtonComponent } from '../../buttons/button/button.component';
import { IconComponent } from '../../icon/icon.component';
import { LanguageComponent } from '../../language/language.component';
import { LoadingComponent } from '../../loading/loading.component';
import { InputComponent } from '../input.component';

export enum InputLanguageSelectorComponentType {
  REGULAR = 'REGULAR',
  LABELED = 'LABELED'
}

export enum InputLanguageSelectorTextSize {
  SMALL = 'SMALL',
  REGULAR = 'REGULAR'
}

@Component({
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    LanguageComponent,
    LoadingComponent,
    ButtonComponent,
    IconComponent,
    IsNonEmptyArrayPipe,
    HasSetValuePipe,
    OverflowTargetDirective
  ],
  selector: 'nrc-input-language-selector',
  templateUrl: './input-language-selector.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [growAnimation, fadeInOutAnimation],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputLanguageSelectorComponent),
      multi: true
    }
  ]
})
export class InputLanguageSelectorComponent extends InputComponent implements OnInit, OnChanges {
  @Input() public override label = '';
  @Input() public options: string[] | ILanguageOption[] = [];
  @Input() public invalidItems: string[] = [];
  @Input() public type: InputLanguageSelectorComponentType = InputLanguageSelectorComponentType.REGULAR;
  @Input() public value: string | null = null;
  @Input() public overflowElement?: HTMLElement;
  @Input() public textSize = InputLanguageSelectorTextSize.REGULAR;
  @Input() public borderClass = 'shadow border-b-1';

  @Output() public languageChanged = new EventEmitter<string>();

  public showOptions = signal<boolean>(false);
  public selectedLanguage = signal<string | null>(null);
  public filteredOptions = new Set<ILanguageOption>([]);

  // Computed properties for handling both string[] and ILanguageOption[] inputs
  public normalizedOptions = signal<ILanguageOption[]>([]);
  public selectedLanguageOption = signal<ILanguageOption | null>(null);

  public buttonType = ButtonType;
  public actionRole = ActionRole;
  public languageSelectorType = InputLanguageSelectorComponentType;
  public languageSelectorTextSize = InputLanguageSelectorTextSize;

  /**
   * @constructor
   */
  constructor() {
    super();
  }

  public override ngOnChanges(changes: SimpleChanges): void {
    super.ngOnChanges(changes);

    // Normalize options when they change
    if (changes['options']) {
      this.normalizeOptions();
    }

    if (changes['formControl']?.firstChange) {
      // Set the selected options based on the given form control value.
      this.selectLanguage(this.formControl.value);
    }

    if (this.value && changes['value']) {
      this.selectLanguage(changes['value'].currentValue);
    }
  }

  /**
   * Normalize options to always work with ILanguageOption[] internally.
   *
   */
  private normalizeOptions(): void {
    const normalized: ILanguageOption[] = [];

    for (const option of this.options) {
      if (typeof option === 'string') {
        // Convert string to ILanguageOption
        normalized.push({
          code: option,
          name: option // Will be resolved by LanguageComponent
        });
      } else {
        // Already an ILanguageOption
        normalized.push(option);
      }
    }

    this.normalizedOptions.set(normalized);
    this.updateFilteredOptions();
  }

  /**
   *
   */
  public override ngOnInit(): void {
    super.ngOnInit();

    // Initialize normalized options
    this.normalizeOptions();
  }

  /**
   * Toggles the visibility of language options by setting the showOptions signal.
   */
  public toggleOptions(): void {
    this.showOptions.set(!this.showOptions());
  }

  /**
   * Updates the selected language, filters the available options, and optionally collapses the options list.
   *
   * @param languageCode The language code to select.
   * @param collapseOptions Collapses the options list after selection.
   *
   */
  public selectLanguage(languageCode: string, collapseOptions = false): void {
    this.selectedLanguage.set(languageCode);

    // Find the corresponding language option
    const selectedOption = this.normalizedOptions().find(opt => opt.code === languageCode);

    this.selectedLanguageOption.set(selectedOption || null);

    this.updateFilteredOptions();

    // Call the value accessor to update the form control value
    this.writeValue(languageCode);
    this.languageChanged.emit(languageCode);

    if (collapseOptions) {
      this.showOptions.set(false);
    }
  }

  /**
   * Updates the filtered options based on the selected language.
   */
  private updateFilteredOptions(): void {
    const selectedCode = this.selectedLanguage();

    this.filteredOptions = new Set(
      this.normalizedOptions().filter(option => option.code !== selectedCode)
    );
  }
}
